import { BOARD_TOOL_META } from "./agent-tool-meta";

export type AgentToolKind =
	| "chat"
	| "read"
	| "mutate"
	| "library"
	| "plan"
	| "app";

/**
 * Single source of tool classification. Used to derive read-only sets,
 * mutation detection, and registry integrity checks against BOARD_TOOL_META
 * and createBoardTools / createChatTools / createLibraryTools /
 * createAppTools.
 */
export const AGENT_TOOL_KINDS = {
	presentOptions: "chat",
	listImageModels: "chat",
	generateImageInChat: "chat",
	loadSkill: "chat",

	getCurrentRoute: "app",
	navigateApp: "app",

	getBoardSnapshot: "read",
	listVisibleCards: "read",
	listSelectedCards: "read",
	getCardContent: "read",
	openCard: "read",
	navigateToCard: "read",
	listPromptCards: "read",
	listPreprintCards: "read",
	listAvailableAgentsAndModels: "read",
	fitBoardToContent: "read",
	openBoardSearch: "read",
	listShapesInViewport: "read",
	listShapesOnPage: "read",
	listSelectedShapes: "read",
	getArrowConnections: "read",
	inspectDiagram: "read",
	getShapeContent: "read",
	navigateToShape: "read",
	listCustomShapes: "read",
	getCustomShapeInfo: "read",
	findEmptySpace: "read",
	exportViewportImage: "read",
	getBoardDocumentSnapshot: "read",
	getCamera: "read",

	createNoteCard: "mutate",
	createPromptCard: "mutate",
	generateFromPromptCard: "mutate",
	generateFromPromptCardAllModels: "mutate",
	createGeoShape: "mutate",
	createTextShape: "mutate",
	createArrow: "mutate",
	createDrawShape: "mutate",
	createHighlightShape: "mutate",
	createLineShape: "mutate",
	createNoteShape: "mutate",
	createFrameShape: "mutate",
	updateGeoShape: "mutate",
	updateArrow: "mutate",
	updateTextShape: "mutate",
	updateNoteShape: "mutate",
	updateFrameShape: "mutate",
	updateDrawShape: "mutate",
	updateLineShape: "mutate",
	updateHighlightShape: "mutate",
	updateImageShape: "mutate",
	moveShape: "mutate",
	resizeShapes: "mutate",
	rotateShapes: "mutate",
	flipShapes: "mutate",
	nudgeShapes: "mutate",
	lockShapes: "mutate",
	unlockShapes: "mutate",
	alignShapes: "mutate",
	distributeShapes: "mutate",
	duplicateShapes: "mutate",
	groupShapes: "mutate",
	ungroupShapes: "mutate",
	moveShapesToFrame: "mutate",
	setShapeZOrder: "mutate",
	createImageShape: "mutate",
	deleteShape: "mutate",
	selectShapes: "mutate",
	setCamera: "mutate",
	moveCard: "mutate",
	resizeCard: "mutate",
	updateCardContent: "mutate",
	deleteCard: "mutate",
	duplicateCard: "mutate",
	lockCard: "mutate",
	unlockCard: "mutate",
	createCardSection: "mutate",
	moveShapesToCardSection: "mutate",

	runBoardPlan: "plan",

	listNavbarTree: "library",
	createWhiteboard: "library",
	renameWhiteboard: "library",
	createFolder: "library",
	renameFolder: "library",
	deleteFolder: "library",
	moveNavbarItem: "library",
	duplicateWhiteboard: "library",
	deleteWhiteboard: "library",
} as const satisfies Record<string, AgentToolKind>;

export type AgentToolName = keyof typeof AGENT_TOOL_KINDS;

export function getAgentToolKind(toolName: string): AgentToolKind | null {
	if (toolName in AGENT_TOOL_KINDS) {
		return AGENT_TOOL_KINDS[toolName as AgentToolName];
	}
	return null;
}

export function getReadOnlyToolNames(): Set<string> {
	return new Set(
		Object.entries(AGENT_TOOL_KINDS)
			.filter(([, kind]) => kind === "read" || kind === "chat")
			.map(([name]) => name),
	);
}

export function getChatOnlyToolNames(): Set<string> {
	return new Set(
		Object.entries(AGENT_TOOL_KINDS)
			.filter(([, kind]) => kind === "chat")
			.map(([name]) => name),
	);
}

export function isAgentBoardMutationToolName(toolName: string) {
	const kind = getAgentToolKind(toolName);
	return kind === "mutate" || kind === "plan";
}

/**
 * Navbar / whiteboard lifecycle writes — not canvas mutate/plan.
 * listNavbarTree is read-only and does not require approval.
 */
export function requiresWhiteboardLifecycleApproval(toolName: string) {
	const kind = getAgentToolKind(toolName);
	return kind === "library" && toolName !== "listNavbarTree";
}

/** Ops allowed inside runBoardPlan (canvas mutations only). */
export function getBoardPlanOpNames(): string[] {
	return Object.entries(AGENT_TOOL_KINDS)
		.filter(([, kind]) => kind === "mutate")
		.map(([name]) => name);
}

export function assertToolRegistryIntegrity(toolNames: string[]) {
	const missingKind: string[] = [];
	const missingMeta: string[] = [];

	for (const name of toolNames) {
		if (!(name in AGENT_TOOL_KINDS)) {
			missingKind.push(name);
		}
		if (!(name in BOARD_TOOL_META)) {
			missingMeta.push(name);
		}
	}

	if (missingKind.length > 0 || missingMeta.length > 0) {
		throw new Error(
			[
				missingKind.length > 0
					? `Missing AGENT_TOOL_KINDS: ${missingKind.join(", ")}`
					: null,
				missingMeta.length > 0
					? `Missing BOARD_TOOL_META: ${missingMeta.join(", ")}`
					: null,
			]
				.filter(Boolean)
				.join("; "),
		);
	}
}
